import { type ReactNode, useCallback, useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Countdown } from '@nesso/ui/primitives/countdown';
import { useInactivityTimer } from '@nesso/ui/providers/inactivity-timer-provider';

import { practiceMemberOptions } from '@/entities/practice-member';
import { useAuth } from '@/entities/user';
import { getConfigValue } from '@/shared/config/utils';
import { subscribeToRecordingActive } from '@/shared/lib/cross-tab-recording-signal';
import {
    InactivitySignOutContext,
    type SignOutCallback,
} from '@/shared/lib/inactivity-sign-out-context';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';

const INACTIVITY_WARNING_ACTION_ID = 'inactivity-warning';
const INACTIVITY_SIGN_OUT_ACTION_ID = 'inactivity-sign-out';
const INACTIVITY_WARNING_TIMEOUT = getConfigValue('INACTIVITY_WARNING_MINUTES') * 60 * 1000;
const INACTIVITY_SIGN_OUT_TIMEOUT = getConfigValue('INACTIVITY_SIGN_OUT_MINUTES') * 60 * 1000;

export function InactivitySignOutProvider({ children }: { children: ReactNode }) {
    const { data: practiceMember } = useQuery(practiceMemberOptions());
    const { scheduleAction, cancelAction, pauseActivityTracking, resumeActivityTracking } =
        useInactivityTimer();
    const openConfirm = useConfirmDialogStore((s) => s.open);
    const dismissConfirm = useConfirmDialogStore((s) => s.dismiss);
    const { signOut } = useAuth();

    const callbacks = useRef<Map<string, SignOutCallback>>(new Map());
    // Refs let the long-lived cross-tab subscription read the latest schedule/cancel
    // without re-subscribing on every render that recreates them.
    const scheduleActionsRef = useRef<() => void>(() => {});
    const cancelActionsRef = useRef<() => void>(() => {});
    const isWarningOpenRef = useRef(false);

    const handleSignOut = useCallback(async () => {
        await Promise.allSettled([...callbacks.current.values()].map((cb) => cb()));
        signOut();
    }, [signOut]);

    const scheduleActions = useCallback(() => {
        scheduleAction({
            id: INACTIVITY_WARNING_ACTION_ID,
            delay: INACTIVITY_WARNING_TIMEOUT,
            action: (elapsed) => {
                if (elapsed < INACTIVITY_SIGN_OUT_TIMEOUT) {
                    const deadlineAt =
                        Date.now() + INACTIVITY_SIGN_OUT_TIMEOUT - INACTIVITY_WARNING_TIMEOUT;

                    isWarningOpenRef.current = true;
                    pauseActivityTracking();
                    openConfirm({
                        title: 'Are you still there?',
                        description: (
                            <>
                                Privacy is important, and you've been gone a while. We will log you
                                out in{' '}
                                <b>
                                    <Countdown deadlineAt={deadlineAt} />
                                </b>{' '}
                                unless you confirm you're still with us.
                            </>
                        ),
                        cancelText: 'Log Out',
                        confirmText: 'Continue Working',
                        variant: 'default',
                        onConfirm: () => {
                            isWarningOpenRef.current = false;
                            resumeActivityTracking();
                            scheduleActionsRef.current();
                        },
                        onCancel: () => {
                            isWarningOpenRef.current = false;
                            handleSignOut();
                        },
                    });
                }
            },
        });
        scheduleAction({
            id: INACTIVITY_SIGN_OUT_ACTION_ID,
            delay: INACTIVITY_SIGN_OUT_TIMEOUT,
            action: handleSignOut,
        });
    }, [scheduleAction, openConfirm, handleSignOut, pauseActivityTracking, resumeActivityTracking]);

    useEffect(() => {
        scheduleActionsRef.current = scheduleActions;
    }, [scheduleActions]);

    const cancelActions = useCallback(() => {
        cancelAction(INACTIVITY_WARNING_ACTION_ID);
        cancelAction(INACTIVITY_SIGN_OUT_ACTION_ID);
    }, [cancelAction]);

    useEffect(() => {
        cancelActionsRef.current = cancelActions;
    }, [cancelActions]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the signed-in practice member changes so timers reset per user
    useEffect(() => {
        scheduleActions();
        return () => {
            cancelActions();
        };
    }, [practiceMember?.id, scheduleActions, cancelActions]);

    useEffect(() => {
        return subscribeToRecordingActive((active) => {
            if (active && isWarningOpenRef.current) {
                isWarningOpenRef.current = false;
                resumeActivityTracking();
                dismissConfirm();
                cancelActionsRef.current();
                scheduleActionsRef.current();
            }
        });
    }, [dismissConfirm, resumeActivityTracking]);

    const registerCallback = useCallback((id: string, callback: SignOutCallback) => {
        callbacks.current.set(id, callback);
    }, []);

    const unregisterCallback = useCallback((id: string) => {
        callbacks.current.delete(id);
    }, []);

    return (
        <InactivitySignOutContext.Provider value={{ registerCallback, unregisterCallback }}>
            {children}
        </InactivitySignOutContext.Provider>
    );
}

export { useInactivitySignOut } from '@/shared/lib/inactivity-sign-out-context';
